import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { products, copy } from "../data/content";

const ProductRow = ({ product, index }) => {
  const { t } = useLanguage();
  const reversed = index % 2 === 1;

  return (
    <motion.article
      data-testid={`product-${product.id}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="grid grid-cols-12 gap-6 md:gap-10 items-center border-t border-[#E5E5E5] py-14 md:py-24"
    >
      {/* Screenshot */}
      <a
        href={product.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`product-image-link-${product.id}`}
        className={`col-span-12 md:col-span-6 group block ${
          reversed ? "md:order-2 md:col-start-7" : "md:order-1"
        }`}
      >
        <div className="relative overflow-hidden aspect-[16/10] bg-[#F2F2F0] border border-[#E5E5E5]">
          <img
            src={product.image}
            alt={t(product.title)}
            loading="lazy"
            className="h-full w-full object-contain object-center scale-100 group-hover:scale-[1.03] transition-transform duration-700 ease-out"
          />
          <span className="absolute top-4 left-4 font-['IBM_Plex_Mono'] text-xs tracking-[0.2em] uppercase bg-[#F9F9F8]/90 backdrop-blur-sm px-3 py-1 text-[#111111]">
            {t(product.category)}
          </span>
        </div>
      </a>

      {/* Text */}
      <div
        className={`col-span-12 md:col-span-5 ${
          reversed ? "md:order-1 md:col-start-1" : "md:order-2 md:col-start-8"
        }`}
      >
        <div className="flex items-baseline gap-3">
          <span className="font-['IBM_Plex_Mono'] text-sm text-[#999999]">
            0{index + 1}
          </span>
          <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl tracking-tight text-[#111111]">
            {t(product.title)}
          </h3>
        </div>

        <p className="mt-5 font-['IBM_Plex_Mono'] text-sm md:text-base leading-relaxed text-[#555555] max-w-md">
          {t(product.description)}
        </p>

        <a
          href={product.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-visit-${product.id}`}
          className="group mt-8 inline-flex items-center gap-2 font-['IBM_Plex_Mono'] text-xs tracking-[0.2em] uppercase text-[#111111] focus:outline-none focus:ring-1 focus:ring-black focus:ring-offset-2"
        >
          <span className="relative">
            {product.name}
            <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#111111] transition-[width] duration-500 group-hover:w-full" />
          </span>
          <ArrowUpRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
      </div>
    </motion.article>
  );
};

export const Products = () => {
  const { t } = useLanguage();

  return (
    <section
      id="products"
      data-testid="products"
      className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32"
    >
      <div className="mb-8 md:mb-16 max-w-3xl">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-['Cormorant_Garamond'] text-4xl md:text-6xl tracking-tight text-[#111111]"
        >
          {t(copy.products.heading)}
        </motion.h2>
        <p className="mt-4 font-['IBM_Plex_Mono'] text-sm md:text-base text-[#555555]">
          {t(copy.products.subheading)}
        </p>
      </div>

      <div>
        {products.map((product, i) => (
          <ProductRow key={product.id} product={product} index={i} />
        ))}
      </div>
    </section>
  );
};
